import { CustomerThreadInfo } from "@/types/bots";
import axiosInstance from "@/utils/axios-setup";
import { supabase } from "@/utils/supabase/client";
import { v4 as uuidv4 } from "uuid";

export const createCustomerThread =
  async (): Promise<CustomerThreadInfo | null> => {
    try {
      const { data, error } = await supabase
        .from("customer_threads")
        .insert({})
        .select()
        .single();
      return data as CustomerThreadInfo | null;
    } catch (error) {
      console.error("Error creating thread:", error);
      return null;
    }
  };

export const uploadFilesToCustomerBucket = async (
  files: any[],
  threadId: string
) => {
  try {
    // Ensure files exist
    if (!files.length) {
      return [];
    }
    const uploadPromises = files.map((file) => uploadFile(file, threadId));
    const documents = await Promise.all(uploadPromises);
    return documents.filter((doc) => doc !== null);
  } catch (error) {
    throw error;
  }
};

//Step 2: Upload files to bucket
const uploadFile = async (file: File, threadId: string) => {
  const filename = `${uuidv4()}-${file.name}`;

  // Check if filename exists
  if (!filename) {
    return null;
  }
  // Check if file is a valid File object
  if (!(file instanceof File)) {
    return null;
  }
  try {
    const { data, error }: any = await supabase.storage
      .from("documents")
      .upload(filename, file, {
        cacheControl: "3600",
        upsert: false,
      });

    if (error) {
      throw error;
    }

    let document = {
      file_id: data.id,
      size: file.size,
      name: file.name,
      path: data.path,
      thread_id: threadId,
    };

    return document;
  } catch (error) {
    throw error;
  }
};

export const uploadFilesToDocumentsTable = async (files: File[]) => {
  try {
    const { data, error } = await supabase
      .from("customer_documents")
      .insert(files)
      .select();
    if (error) {
      throw error;
    }
    return data;
  } catch (error) {
    throw error;
  }
};

export const getPublicUrls = (path: string) => {
  try {
    const { data } = supabase.storage.from("documents").getPublicUrl(path);
    return data;
  } catch (error) {
    throw error;
  }
};

export const uploadFiles = async (UploadedFiles: File[]) => {
  try {
    //Contstruct requestbody
    let request = UploadedFiles.map((file: any) => {
      let fileUrl: any = getPublicUrls(file.path);
      if (fileUrl) {
        return {
          id: file.id,
          thread_id: file.thread_id,
          file_url: fileUrl.publicUrl,
        };
      }
    });
    const response = await axiosInstance.post(
      `${process.env.NEXT_PUBLIC_EDGE_FUNCTION_BASE_URL}/customer/file-upload` || "",
      { data: request },
      {
        headers: { "Content-Type": "application/json" },
      }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const subscribeToCustomerDocuments = async (
  threadId: string,
  handlePayload: (payload: any) => void
) => {
  try {
    const channel = supabase
      .channel("file_uploads")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "customer_documents",
          filter: `thread_id=eq.${threadId}`,
        },
        (payload) => {
          if (["COMPLETED", "FAILED","INPROGRESS"].includes(payload.new.status)) {
            handlePayload(payload.new);
          }
        }
      )
      .subscribe();

    return channel; // Return the channel for cleanup
  } catch (error) {
    console.error("Error subscribing to channel:", error);
    throw error;
  }
};

export const subscribeToChats = async (
  threadId: string,
  handlePayload: (payload: any) => void
) => {
  try {
    const channel = await supabase
      .channel("customer_chat_history")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "customer_chat_history",
          filter: `thread_id=eq.${threadId}`,
        },
        (payload) => {
          handlePayload(payload.new);
        }
      )
      .subscribe();
    return channel;
  } catch (error) {
    throw error;
  }
};

export const subscribeToTitles = async (
  threadId: string,
  handlePayload: (payload: any) => void
) => {
  try {
    const channel = await supabase
      .channel("customer_threads_title")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "customer_threads",
          filter: `id=eq.${threadId}`,
        },
        (payload) => {
          handlePayload(payload.new);
        }
      )
      .subscribe();
    return channel;
  } catch (error) {
    throw error;
  }
};

export const getPromptsInfo = async (prompts: any[]) => {
  try {
    const results: any[] = await Promise.all(
      prompts.map(async (prompt: any) => {
        const { data, error } = await supabase
          .from("customer_prompts")
          .select("*")
          .eq("id", prompt.id)
          .order("rank", { ascending: true });
        if (error) {
          console.error("Error fetching prompt:", error);
          return null;
        }
        return data;
      })
    );
    results.filter((item) => item !== null);
    return [].concat(...results);
  } catch (error) {
    console.error("Error fetching prompts:", error);
    throw error;
  }
};

export const updateChatHistory = async (
  req: string,
  res: string,
  thread: string
) => {
  try {
    const { data, error } = await supabase
      .from("customer_chat_history")
      .insert([
        {
          response: res,
          request: req,
          thread_id: thread,
          status: "INPROGRESS",
        },
      ])
      .select("*");
    if (error) {
      throw error;
    }
    return data[0].id;
  } catch (error) {
    throw error;
  }
};

export const interact = async (
  prompt: string,
  documents: number[],
  thread_id: string,
  chat_id: string
) => {
  try {
    const response = await axiosInstance.post(
      `${process.env.NEXT_PUBLIC_EDGE_FUNCTION_BASE_URL}/customer/chat`,
      {
        prompt,
        documents,
        thread_id,
        chat_id,
      }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const LoadAllThreads = async (userId: string) => {
  const { data, error } = await supabase
    .from("customer_threads")
    .select("*, customer_documents (*)")
    .eq("created_by", userId)
    .order("created_at", { ascending: false });

  if (error) {
    throw error;
  }

  return data;
};

export const getThread = async (threadId: string) => {
  const { data, error } = await supabase
    .from("customer_threads")
    .select("*")
    .eq("thread_uuid", threadId)
    .single();
  if (error) {
    throw error;
  }
  return data;
};

export const fetch_uuid = async (thread_id:any) => {
  try {
    const { data, error } = await supabase
      .from("customer_threads")
      .select("thread_uuid")
      .eq('id', thread_id)
      .single();

    if (error) {
      throw new Error(error.message);
    }

    return data.thread_uuid;
  } catch (error) {
    console.error("Error fetching chat ID:", error);
  }
}

export const loadChatHistory = async (threadId: string) => {
  const { data, error } = await supabase
    .from("customer_chat_history")
    .select("*")
    .eq("thread_id", threadId)
    .order("created_at", { ascending: true });

  if (error) {
    throw error;
  }

  return data;
};

export const deleteAthread = async (threadId: string) => {
  const { error } = await supabase
    .from("customer_threads")
    .delete()
    .eq("id", threadId);
  if (error) {
    throw error;
  }
};

export const subscribeToFileUpload = async (
  threadId: string,
  handlePayload: (payload: any) => void
) => {
  try {
    const channel = await supabase
      .channel("file_uploads")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "customer_documents",
          filter: `thread_id=eq.${threadId}`,
        },
        (payload) => {
          if (payload.new.status != "CREATED") {
            handlePayload(payload.new);
          }
        }
      )
      .subscribe();
    return channel;
  } catch (error) {
    console.log(error);
  }
};

export const fetchEmbeddedIds = async (file_id: string): Promise<string[]> => {
  const { data, error } = await supabase
    .from("customer_documents")
    .select("embedded_ids")
    .eq("id", file_id)
    .single();

  if (error) {
    throw new Error(`Failed to fetch embedded IDs: ${error.message}`);
  }

  if (!data || !data.embedded_ids) {
    throw new Error("No embedded IDs found for the provided file ID");
  }
  return data.embedded_ids;
};

export const callFileDeletionAPI = async (embedding_ids: string[]): Promise<void> => {
  if (!Array.isArray(embedding_ids) || embedding_ids.length === 0) {
    throw new Error("Invalid or empty embedding IDs provided");
  }
  let data: any = { embedded_ids: embedding_ids };
  const config = {
    headers: {
      "Content-Type": "application/json",
    },
    data,
  };
  const response = await axiosInstance.delete(
    `${process.env.NEXT_PUBLIC_EDGE_FUNCTION_BASE_URL}/customer/file-delete`,
    config,
  );

  if (response.status !== 200) {
    throw new Error(`File deletion API failed with status code: ${response.status}`);
  }
};

export const deleteFromCustomerPrompts = async (file_id: string): Promise<void> => {
  const { error } = await supabase
    .from("customer_prompts")
    .delete()
    .eq("file_id", file_id);

  if (error) {
    throw new Error(`Failed to delete from customer_prompts: ${error.message}`);
  }
};

export const deleteFromCustomerDocuments = async (file_id: string): Promise<void> => {
  const { error } = await supabase
    .from("customer_documents")
    .delete()
    .eq("id", file_id);

  if (error) {
    throw new Error(`Failed to delete from customer_documents: ${error.message}`);
  }
};
export const handleFileDelete = async (file_id: string): Promise<void> => {
  try {
    const embeddedIds = await fetchEmbeddedIds(file_id);
    await callFileDeletionAPI(embeddedIds);
    await deleteFromCustomerPrompts(file_id);
    await deleteFromCustomerDocuments(file_id);
  } catch (error: any) {
    throw error;
  }
};

